import "shared/ui/toolbar/tools/layertreeAndEdit/layerContent/layerContent.css";

import { apm } from "@elastic/apm-rum";
import { mm } from "@inovitas/infra3dsdk";
import { IFeatureStyle } from "@inovitas/infra3dsdk/dist/types/mm/featuretree/interfaces/IFeatureStyle";
import { Delete, SettingsOutlined } from "@mui/icons-material";
import {
  Autocomplete,
  Box,
  Button,
  FormControl,
  IconButton,
  InputLabel,
  MenuItem,
  Select,
  TextField,
  Typography,
} from "@mui/material";
import React, { useEffect, useState } from "react";

import { monitor } from "shared/common/services/apm-logging";
import { defaultStore } from "shared/common/stores/defaultStore";
import { sidebarStore } from "shared/common/stores/sidebarStore";
import { Property } from "./CreateLayer";
import LayerSettings from "./layerSettings/LayerSettings";
import { useTranslation } from "react-i18next";

export default function EditWebServiceLayers() {
  const darkTheme = defaultStore((state) => state.darkTheme);

  const layerGroups = sidebarStore((state) => state.layerGroups);
  const changeLayerGroups = sidebarStore((state) => state.changeLayerGroups);
  const webServiceLayer = sidebarStore((state) => state.webServiceLayer);
  const changeWebServiceLayer = sidebarStore(
    (state) => state.changeWebServiceLayer
  );
  const changeSaveButtonStateOnAddNewLayers = sidebarStore(
    (state) => state.changeSaveButtonStateOnAddNewLayers
  );

  const { t } = useTranslation();

  const defaultPointStyle: IFeatureStyle = {
    stylename: "layerstyle",
    rules: [
      {
        filter: "",
        symbolizer: "point",
        params: {
          size: 20,
          stroke: [0.05, 0.57, 0.82, 1],
          "font-marker": {
            name: "circle",
            rotation: 0,
            fontsize: 20,
            anchor: ["center", "middle"],
          },
        },
      },
    ],
  };

  const defaultLineStyle: IFeatureStyle = {
    stylename: "layerstyle",
    rules: [
      {
        filter: "",
        symbolizer: "point",
        params: {
          size: 20,
          stroke: [0.05, 0.57, 0.82, 1],
          "font-marker": {
            name: "circle",
            rotation: 0,
            fontsize: 20,
            anchor: ["center", "middle"],
          },
        },
      },
      {
        filter: "",
        symbolizer: "line",
        params: {
          "stroke-width": 4,
          stroke: [0.05, 0.57, 0.82, 1],
        },
      },
    ],
  };

  const defaultPolygonStyle: IFeatureStyle = {
    stylename: "layerstyle",
    rules: [
      {
        filter: "",
        symbolizer: "point",
        params: {
          size: 20,
          stroke: [0.05, 0.57, 0.82, 1],
          "font-marker": {
            name: "circle",
            rotation: 0,
            fontsize: 20,
            anchor: ["center", "middle"],
          },
        },
      },
      {
        filter: "",
        symbolizer: "line",
        params: {
          "stroke-width": 4,
          stroke: [0.05, 0.57, 0.82, 1],
        },
      },
      {
        filter: "",
        symbolizer: "polygon",
        params: {
          fill: [0.77, 0.86, 0.91, 0.4],
        },
      },
    ],
  };

  const [showSettings, setShowSettings] = useState<boolean>(false);
  const [propertyRows, setPropertyRows] = useState<Property[]>([
    { name: "ID", datatype: mm.LAYERPROPERTYTYPE.INTEGER },
  ]);

  const [itemSelected, setItemSelected] = useState<any>(); // TODO: Type

  //check if can save layer changes
  const checkIfCanSave = () => {
    if (webServiceLayer.layers) {
      let canSave = true;
      webServiceLayer.layers.forEach((item: mm.Layer) => {
        if (item?.geometrytype === -1) {
          canSave = false;
        }
      });
      changeSaveButtonStateOnAddNewLayers(canSave);
    }
  };

  //delete layer
  const deleteLayer = (layer: mm.Layer) => {
    if (webServiceLayer.layers) {
      const layerString = JSON.stringify(layer);
      webServiceLayer.layers = webServiceLayer.layers.filter(
        (item: mm.Layer) => JSON.stringify(item) !== layerString
      );
      const afterChanges = { ...webServiceLayer };
      changeWebServiceLayer(afterChanges);
    }
  };

  //change layer group
  const changeGroup = (layer: mm.Layer, value: string) => {
    layer.topic = value;
    const afterChanges = { ...webServiceLayer };
    changeWebServiceLayer(afterChanges);
  };

  //change layer geometry type
  const changeGeometry = (layer: mm.Layer, value: mm.GEOMETRYTYPE) => {
    layer.geometrytype = value;
    fillSettingsDatas(layer);
    const afterChanges = { ...webServiceLayer };
    changeWebServiceLayer(afterChanges);
  };

  //select layer to edit and open edit layer window
  const selectLayerToEdit = (layer: mm.Layer) => {
    fillSettingsDatas(layer);
    setItemSelected(layer);
    setShowSettings(true);
  };

  //update layer properties
  const updateProperties = () => {
    if (propertyRows && itemSelected) {
      const propertydef = propertyRows.filter(
        (item) => item.name !== "" && item.datatype !== ""
      );
      itemSelected.propertydef = propertydef;
    }
  };

  //reset settings changes
  const resetSettingsChanges = () => {
    setPropertyRows([{ name: "ID", datatype: mm.LAYERPROPERTYTYPE.INTEGER }]);
  };

  // fill settings window with default datas
  const fillSettingsDatas = (layer: any) => {
    // TODO: Type
    if (layer?.propertydef !== undefined) {
      setPropertyRows(layer.propertydef);
    } else if (layer.propertydef !== undefined) {
      const propertydef: Property[] = [];
      layer.propertydef.forEach((item: Property) => {
        let type: mm.LAYERPROPERTYTYPE | -1 = -1;
        switch (item.datatype) {
          case "string":
            type = mm.LAYERPROPERTYTYPE.STRING;
            break;
          case "number":
            type = mm.LAYERPROPERTYTYPE.NUMBER;
            break;
          case "enum":
            type = mm.LAYERPROPERTYTYPE.ENUM;
            break;
          case "boolean":
            type = mm.LAYERPROPERTYTYPE.BOOLEAN;
            break;
          case "date":
            type = mm.LAYERPROPERTYTYPE.DATE;
            break;
          case "integer":
            type = mm.LAYERPROPERTYTYPE.INTEGER;
            break;
        }
        propertydef.push({ name: item.name, datatype: type });
      });
      layer.propertydef = propertydef;
      setPropertyRows(propertydef);
    } else {
      layer.propertydef = propertyRows;
    }

    if (!layer.style) {
      if (
        layer.geometrytype === mm.GEOMETRYTYPE.POLYGON ||
        layer.geometrytype === mm.GEOMETRYTYPE.MULTIPOLYGON
      ) {
        layer.style = defaultPolygonStyle;
      } else if (
        layer.geometrytype === mm.GEOMETRYTYPE.LINESTRING ||
        layer.geometrytype === mm.GEOMETRYTYPE.MULTILINESTRING
      ) {
        layer.style = defaultLineStyle;
      } else if (
        layer.geometrytype === mm.GEOMETRYTYPE.POINT ||
        layer.geometrytype === mm.GEOMETRYTYPE.MULTIPOINT
      ) {
        layer.style = defaultPointStyle;
      }
    }
  };

  //save setting chnages
  const saveSettingsChanges = () => {
    if (webServiceLayer.layers) {
      webServiceLayer.layers.filter(
        (layer: mm.Layer) =>
          layer.layername === itemSelected.layername &&
          layer.topic === itemSelected.topic &&
          layer.geometrytype === itemSelected.geometrytype
      )[0] = { ...itemSelected };

      checkIfCanSave();
      setShowSettings(false);
      resetSettingsChanges();
      setItemSelected(undefined);
    }
  };

  //call update properties function
  useEffect(() => {
    updateProperties();
  }, [propertyRows]);

  //call update properties function if have item selected
  useEffect(() => {
    if (itemSelected) updateProperties();
  }, [itemSelected]);

  //check if can save
  useEffect(() => {
    checkIfCanSave();
  }, [webServiceLayer]);

  //fill settings data first time
  useEffect(() => {
    if (webServiceLayer.layers) {
      webServiceLayer.layers.forEach((layer: mm.Layer) => {
        fillSettingsDatas(layer);
      });
    }
  }, []);

  return (
    <Box
      display={"flex"}
      flexDirection={"column"}
      width={"100%"}
      height={"calc(100% - 16px)"}
      overflow={"auto"}
      paddingY={1}
      gap={3}
    >
      {(itemSelected === undefined || !showSettings) && (
        <TextField
          label="Connection Name"
          variant="outlined"
          size="small"
          disabled
          value={
            webServiceLayer?.name ? webServiceLayer.name : "Name of connection"
          }
        />
      )}
      {!showSettings && (
        <Typography
          fontWeight={600}
          fontSize={20}
          color={darkTheme ? "white" : "darkblue.main"}
        >
          {t("create_layer.add_available_layers")}
        </Typography>
      )}
      {!showSettings && webServiceLayer.layers && (
        <Box display={"flex"} flexDirection={"column"} gap={2}>
          {webServiceLayer.layers.map((item: mm.Layer, index: number) => (
            <Box
              key={item.title}
              display={"flex"}
              alignItems={"center"}
              gap={2}
              sx={{
                flexDirection: { md: "row" },
                flexWrap: { xs: "wrap", md: "nowrap" },
                paddingBottom: { xs: "16px", md: "0px" },
                borderBottom: {
                  xs:
                    webServiceLayer.layers &&
                    webServiceLayer.layers.length - 1 > index
                      ? "2px #f3f3f3 solid"
                      : "0px",
                  md: "0px",
                },
              }}
            >
              <TextField
                label="Name"
                variant="outlined"
                size="small"
                defaultValue={item.title}
                onChange={(event) => (item.title = event.target.value)}
                sx={{ width: { xs: "100%", md: "auto" } }}
              />
              <Autocomplete
                freeSolo
                disabled={false}
                sx={{
                  width: { xs: "100%", md: "25%" },
                }}
                size="small"
                options={layerGroups}
                noOptionsText={t("create_layer.group_helper")}
                value={item.topic ?? ""}
                getOptionLabel={(option) => option}
                onBlur={(event: React.FocusEvent<HTMLInputElement>) => {
                  changeGroup(item, event.target.value);
                  if (
                    !layerGroups.includes(event.target.value) &&
                    event.target.value !== ""
                  ) {
                    changeLayerGroups(layerGroups.concat(event.target.value));
                  }
                }}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    label={t("create_layer.group")}
                    variant="outlined"
                  />
                )}
              />
              <FormControl
                size="small"
                sx={{
                  width: { xs: "65%", md: "25%" },
                }}
              >
                <InputLabel>{t("create_layer.geometry_type")}</InputLabel>
                <Select
                  disabled={true}
                  label="Geometry Type"
                  defaultValue={item.geometrytype}
                  onChange={(event) =>
                    changeGeometry(item, event.target.value as mm.GEOMETRYTYPE)
                  }
                >
                  <MenuItem value={mm.GEOMETRYTYPE.POINT}>
                    {t("create_layer.geometry_type_1")}
                  </MenuItem>
                  <MenuItem value={mm.GEOMETRYTYPE.MULTIPOINT}>
                    {t("create_layer.geometry_type_4")}
                  </MenuItem>
                  <MenuItem value={mm.GEOMETRYTYPE.LINESTRING}>
                    {t("create_layer.geometry_type_2")}
                  </MenuItem>
                  <MenuItem value={mm.GEOMETRYTYPE.MULTILINESTRING}>
                    {t("create_layer.geometry_type_5")}
                  </MenuItem>
                  <MenuItem value={mm.GEOMETRYTYPE.POLYGON}>
                    {t("create_layer.geometry_type_3")}
                  </MenuItem>
                  <MenuItem value={mm.GEOMETRYTYPE.MULTIPOLYGON}>
                    {t("create_layer.geometry_type_6")}
                  </MenuItem>
                </Select>
              </FormControl>
              <IconButton
                disabled={item?.geometrytype === -1}
                onClick={() =>
                  monitor(
                    apm,
                    "edit-added-webServiceLayer | Add",
                    () => selectLayerToEdit(item),
                    { layername: item.layername }
                  )
                }
                sx={{
                  color: darkTheme ? "white" : "darkblue.main",
                  "@media (hover: hover)": {
                    ":hover": { color: "secondary.main" },
                  },
                }}
              >
                <SettingsOutlined />
              </IconButton>
              <IconButton
                disabled={
                  webServiceLayer.layers && webServiceLayer.layers.length === 1
                }
                onClick={() =>
                  monitor(apm, "delete-added-webServiceLayer | Add", () =>
                    deleteLayer(item)
                  )
                }
                sx={{
                  color: darkTheme ? "white" : "darkblue.main",
                  "@media (hover: hover)": {
                    ":hover": { color: "secondary.main" },
                  },
                }}
              >
                <Delete />
              </IconButton>
            </Box>
          ))}
        </Box>
      )}
      {itemSelected && showSettings && (
        <Box
          display={"flex"}
          sx={{ flexDirection: { xs: "column", md: "row" } }}
          gap={2}
        >
          <TextField
            disabled
            value={itemSelected.title}
            label="Name"
            variant="outlined"
            size="small"
            sx={{
              width: { xs: "100%", md: "25%" },
            }}
          />
          <TextField
            disabled
            value={itemSelected.topic}
            label="Group"
            variant="outlined"
            size="small"
            sx={{
              width: { xs: "100%", md: "25%" },
            }}
          />
          <FormControl
            sx={{
              width: { xs: "100%", md: "25%" },
            }}
            size="small"
          >
            <InputLabel>{t("create_layer.geometry_type")}</InputLabel>
            <Select
              disabled
              label="Geometry Type"
              value={itemSelected.geometrytype}
            >
              <MenuItem value={mm.GEOMETRYTYPE.POINT}>
                {t("create_layer.geometry_type_1")}
              </MenuItem>
              <MenuItem value={mm.GEOMETRYTYPE.MULTIPOINT}>
                {t("create_layer.geometry_type_4")}
              </MenuItem>
              <MenuItem value={mm.GEOMETRYTYPE.LINESTRING}>
                {t("create_layer.geometry_type_2")}
              </MenuItem>
              <MenuItem value={mm.GEOMETRYTYPE.MULTILINESTRING}>
                {t("create_layer.geometry_type_5")}
              </MenuItem>
              <MenuItem value={mm.GEOMETRYTYPE.POLYGON}>
                {t("create_layer.geometry_type_3")}
              </MenuItem>
              <MenuItem value={mm.GEOMETRYTYPE.MULTIPOLYGON}>
                {t("create_layer.geometry_type_6")}
              </MenuItem>
            </Select>
          </FormControl>
          <Button
            onClick={() =>
              monitor(apm, "save-added-webServiceLayer | Add", () =>
                saveSettingsChanges()
              )
            }
            sx={{ backgroundColor: darkTheme ? "white" : "darkblue.main" }}
            variant="contained"
          >
            {t("create_layer.save_settings")}
          </Button>
        </Box>
      )}
      {showSettings && itemSelected.style && (
        <LayerSettings
          editProperties={false} // Used to disable the editpropertyrows
          itemSelected={itemSelected}
          infra3dstyles={itemSelected.style}
          propertyRows={propertyRows}
          setPropertyRows={setPropertyRows}
          checkIfCanSave={updateProperties}
        />
      )}
    </Box>
  );
}
